import { useMemo } from 'react';
import { getExperimentationSettingsQuery } from '@gonfalon/rest-api/queries/experiments';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { conditionalSuspenseQuery } from '@gonfalon/suspense/conditionalSuspenseQuery';
import { isSuspenseQueryEnabled } from '@gonfalon/suspense/isSuspenseQueryEnabled';
import { useSuspenseQueries } from '@tanstack/react-query';

import { getCommonRandomizationUnits } from './getCommonRandomizationUnits';
import { useMetricsByKeysWithGroups } from './useMetricsByKeysWithGroups';

interface UseGuardedReleaseContextKindsOptions {
  metricKeys?: string[];
  metricGroupKeys?: string[];
}

/**
 * Hook to get available context kinds for guarded rollouts.
 *
 * Context kinds are filtered based on:
 * 1. Experimentation settings (only randomization units enabled for experimentation)
 * 2. Selected metrics (only context kinds that are common across all selected metrics)
 *
 * @param enabled - Whether to enable the query (useful for conditional rendering)
 * @param options - Options containing metric keys and metric group keys
 */
export function useGuardedReleaseContextKinds(enabled: boolean, options: UseGuardedReleaseContextKindsOptions = {}) {
  const { metricKeys, metricGroupKeys } = options;
  const projectKey = useProjectKey();

  const [experimentationSettingsQueryResult] = useSuspenseQueries({
    queries: [
      conditionalSuspenseQuery({
        query: getExperimentationSettingsQuery({ projectKey }),
        enabled,
      }),
    ],
  });

  const { metrics: allMetrics, isPending } = useMetricsByKeysWithGroups({ metricKeys, metricGroupKeys });

  // Only use context kinds that are enabled for experimentation
  const baseContextKinds = useMemo(() => {
    if (!isSuspenseQueryEnabled(experimentationSettingsQueryResult)) {
      return [];
    }
    return (
      experimentationSettingsQueryResult.data.randomizationUnits
        ?.filter((unit): unit is typeof unit & { randomizationUnit: string } => unit.randomizationUnit !== undefined)
        .map((unit) => ({
          key: unit.randomizationUnit,
          name: unit._displayName ?? unit.randomizationUnit,
        })) ?? []
    );
  }, [experimentationSettingsQueryResult]);

  // If metrics are still loading or no metrics are selected, return all base context kinds
  if (isPending || allMetrics.length === 0) {
    return {
      contextKinds: baseContextKinds,
    };
  }

  // Filter context kinds to only those common across all selected metrics
  const commonRandomizationUnits = getCommonRandomizationUnits(allMetrics);
  const allowedContextKinds = baseContextKinds.filter((ck) => commonRandomizationUnits.includes(ck.key));

  return {
    contextKinds: allowedContextKinds,
  };
}
